import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signOutUser, getCurrentUser } from "../firebase/authService";
import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";

const COLORS = {
  bg: '#FFFFFF',
  border: '#F0EDE8',
  charcoal: '#1a1a2e',
  muted: '#8E8E9A',
  amber: '#D4A853',
  amberGlow: 'rgba(212,168,83,0.15)',
  cream: '#FFFBF5',
  searchBg: '#F5F5F7',
};

const FONT = "'Inter', system-ui, -apple-system, sans-serif";

const Header = () => {
  const [userName, setUserName] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      setUserName(user.displayName || "Admin");
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await signOutUser();
      navigate("/login");
    } catch (error) {
      console.error("Failed to logout", error);
    }
  };

  const handleLogoutHome = async () => {
    try {
      await signOutUser();
      navigate("/");
    } catch (error) {
      console.error("Failed to logout", error);
    }
  };

  const handlesidebar = () => {
    document.body.classList.toggle("mini-sidebar");
    setSidebarOpen((prev) => !prev);
  };

  const handlesidebarmobilemenu = () => {
    document.body.classList.toggle("slide-nav");
    document.getElementsByTagName("html")[0].classList.toggle("menu-opened");
    document
      .getElementsByClassName("sidebar-overlay")[0]
      ?.classList.toggle("opened");
  };

  const userInitial = userName ? userName.charAt(0).toUpperCase() : "A";

  return (
    <div className="main-wrapper">
      <motion.div
        className="header"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{
          background: COLORS.bg,
          borderBottom: `1px solid ${COLORS.border}`,
          boxShadow: scrolled
            ? '0 4px 20px rgba(0,0,0,0.06)'
            : 'none',
          fontFamily: FONT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          height: 64,
          transition: 'box-shadow 0.3s ease',
        }}
      >
        {/* ========== Left Section ========== */}
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{ display: 'flex', alignItems: 'center', gap: 12 }}
        >
          {/* Logo */}
          <div
            className="header-left"
            style={{
              background: 'transparent',
              borderBottom: 'none',
              padding: 0,
              width: 'auto',
            }}
          >
            <Link
              to="/"
              onClick={handleLogoutHome}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <img
                src="/assets/img/webp/logo02.png"
                alt="Pambazuka"
                style={{ height: 36, width: 'auto' }}
              />
            </Link>
          </div>

          {/* Sidebar Toggle with rotation animation */}
          <motion.button
            onClick={handlesidebar}
            animate={{ rotate: sidebarOpen ? 0 : 90 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            whileHover={{ backgroundColor: COLORS.searchBg }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FeatherIcon icon="menu" size={20} style={{ color: COLORS.charcoal }} />
          </motion.button>

          {/* Breadcrumb placeholder */}
          <div
            style={{
              marginLeft: 4,
              fontSize: 13,
              color: COLORS.muted,
              fontFamily: FONT,
              userSelect: 'none',
            }}
          />
        </motion.div>

        {/* ========== Center: Search ========== */}
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          style={{
            flex: '0 1 auto',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <motion.div
            animate={{
              width: searchFocused ? 380 : 300,
              borderColor: searchFocused ? COLORS.amber : 'transparent',
              boxShadow: searchFocused
                ? `0 0 0 3px ${COLORS.amberGlow}`
                : '0 0 0 0px transparent',
            }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: COLORS.searchBg,
              borderRadius: 50,
              padding: '8px 16px',
              border: '1.5px solid transparent',
            }}
          >
            <FeatherIcon
              icon="search"
              size={16}
              style={{
                color: searchFocused ? COLORS.amber : COLORS.muted,
                transition: 'color 0.2s ease',
                flexShrink: 0,
              }}
            />
            <input
              type="text"
              placeholder="Search anything..."
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13.5,
                fontFamily: FONT,
                color: COLORS.charcoal,
                width: '100%',
                lineHeight: 1.4,
              }}
            />
          </motion.div>
        </motion.div>

        {/* ========== Right Section ========== */}
        <motion.div
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          {/* Mobile toggle */}
          <button
            className="mobile_btn"
            onClick={handlesidebarmobilemenu}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              display: 'none',
            }}
          >
            <FeatherIcon icon="menu" size={20} style={{ color: COLORS.charcoal }} />
          </button>

          {/* Notification Bell */}
          <motion.button
            whileHover={{ backgroundColor: COLORS.searchBg }}
            style={{
              position: 'relative',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              width: 38,
              height: 38,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FeatherIcon icon="bell" size={18} style={{ color: COLORS.charcoal }} />
            {/* Amber notification badge dot */}
            <span
              style={{
                position: 'absolute',
                top: 8,
                right: 9,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: COLORS.amber,
                border: `2px solid ${COLORS.bg}`,
              }}
            />
          </motion.button>

          {/* Quick Action Button */}
          <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            style={{
              background: `linear-gradient(135deg, ${COLORS.amber}, #E08A5A)`,
              border: 'none',
              cursor: 'pointer',
              width: 34,
              height: 34,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 2px 8px ${COLORS.amberGlow}`,
            }}
          >
            <FeatherIcon icon="plus" size={16} style={{ color: '#FFFFFF' }} />
          </motion.button>

          {/* Vertical Divider */}
          <div
            style={{
              width: 1,
              height: 28,
              background: COLORS.border,
              margin: '0 6px',
            }}
          />

          {/* User Area */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'transparent',
                border: 'none',
                borderRadius: 12,
                padding: '5px 10px 5px 5px',
                cursor: 'pointer',
                fontFamily: FONT,
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = COLORS.searchBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {/* Avatar circle with gradient */}
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${COLORS.amber}, #E08A5A)`,
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                  fontFamily: FONT,
                  flexShrink: 0,
                }}
              >
                {userInitial}
              </div>
              {/* Name and role */}
              <div style={{ textAlign: 'left' }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: COLORS.charcoal,
                    lineHeight: 1.2,
                    fontFamily: FONT,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userName}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: COLORS.muted,
                    fontFamily: FONT,
                    lineHeight: 1.2,
                  }}
                >
                  Admin
                </div>
              </div>
              {/* Chevron with rotation */}
              <FeatherIcon
                icon="chevron-down"
                size={14}
                style={{
                  color: COLORS.muted,
                  marginLeft: 2,
                  transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.25s ease',
                }}
              />
            </button>

            {/* Dropdown with AnimatePresence */}
            <AnimatePresence>
              {dropdownOpen && (
                <>
                  {/* Invisible backdrop to close dropdown */}
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 998,
                    }}
                    onClick={() => setDropdownOpen(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      background: '#FFFFFF',
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 14,
                      boxShadow: '0 12px 36px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.04)',
                      padding: 6,
                      zIndex: 999,
                      minWidth: 180,
                      fontFamily: FONT,
                    }}
                  >
                    {/* Profile link (disabled placeholder) */}
                    <button
                      disabled
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        width: '100%',
                        padding: '10px 14px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        color: COLORS.muted,
                        fontFamily: FONT,
                        cursor: 'default',
                        opacity: 0.6,
                      }}
                    >
                      <FeatherIcon icon="user" size={16} />
                      <span>Profile</span>
                    </button>

                    {/* Divider */}
                    <div
                      style={{
                        height: 1,
                        background: COLORS.border,
                        margin: '4px 10px',
                      }}
                    />

                    {/* Logout button (red) */}
                    <button
                      onClick={handleLogout}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        width: '100%',
                        padding: '10px 14px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#E05A5A',
                        fontFamily: FONT,
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(224,90,90,0.07)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <FeatherIcon icon="log-out" size={16} />
                      <span>Logout</span>
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Header;
